(() => {
  if (window.__tathagatServicesTypeBooted) return;
  window.__tathagatServicesTypeBooted = true;

  const MAX_SHIFT = 12;
  const MAX_ROTATE = 3;
  const COMPACT_SCALE = 0.62;

  // Scroll-driven page colour: hero ivory, services pale coral, approach pale lavender, closing soft mint.
  const SECTION_COLORS = [
    ['#top', [248, 241, 228]],
    ['#offers', [252, 229, 223]],
    ['#approach', [238, 233, 250]],
    ['#contact', [217, 240, 194]]
  ];

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const lerp = (a, b, t) => a + (b - a) * t;

  const initialize = (root) => {
    if (!root || root.dataset.servicesTypeInitialized === 'true') return;
    const layer = root.querySelector('[data-services-type-layer]');
    if (!layer) return;
    root.dataset.servicesTypeInitialized = 'true';

    const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compactQuery = window.matchMedia('(max-width: 820px)');
    const hero = layer.parentElement;
    const offers = root.querySelector('#offers');
    const title = root.querySelector('.services-type-title');
    const cards = [...root.querySelectorAll('[data-service-card]')];
    const colorStops = SECTION_COLORS
      .map(([selector, color]) => [root.querySelector(selector), color])
      .filter(([element]) => element);
    let frame = 0;
    let observer;

    // Each card's accent line uses that service's own colour.
    cards.forEach((card) => {
      if (card.dataset.color) card.style.setProperty('--st-accent', card.dataset.color);
    });

    // Reveal-on-view elements (closing word sequence).
    const revealTargets = root.querySelectorAll('[data-services-type-reveal]');
    if ('IntersectionObserver' in window) {
      root.classList.add('services-type-ready');
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.25 });
      revealTargets.forEach((target) => observer.observe(target));
    } else {
      revealTargets.forEach((target) => target.classList.add('is-in'));
    }

    // Measure the free bands around the headline so floating words never overlap it.
    const heroTop = hero.querySelector('.services__hero-top');
    const heroCopy = hero.querySelector('.services__hero-copy');
    const banner = hero.querySelector('.services__check-banner');
    const measureBands = () => {
      if (!heroTop || !heroCopy || !banner) return;
      const band1 = (heroTop.offsetTop + heroTop.offsetHeight + heroCopy.offsetTop) / 2;
      const band2 = (heroCopy.offsetTop + heroCopy.offsetHeight + banner.offsetTop) / 2;
      layer.style.setProperty('--st-band1-mid', `${band1.toFixed(1)}px`);
      layer.style.setProperty('--st-band2-mid', `${band2.toFixed(1)}px`);
    };
    measureBands();
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measureBands) : null;
    resizeObserver?.observe(hero);
    window.addEventListener('resize', measureBands);
    document.fonts?.ready.then(measureBands);

    // Pointer: moves only the type layer's own wrapper, never the SVG artwork.
    const onPointerMove = (event) => {
      if (reduceQuery.matches) return;
      const bounds = hero.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const x = clamp((event.clientX - bounds.left) / bounds.width - 0.5, -0.5, 0.5) * 2;
      const y = clamp((event.clientY - bounds.top) / bounds.height - 0.5, -0.5, 0.5) * 2;
      const scale = compactQuery.matches ? COMPACT_SCALE : 1;
      layer.style.setProperty('--services-type-x', `${(x * MAX_SHIFT * scale).toFixed(2)}px`);
      layer.style.setProperty('--services-type-y', `${(y * MAX_SHIFT * scale).toFixed(2)}px`);
      layer.style.setProperty('--services-type-r', `${(x * MAX_ROTATE * scale).toFixed(2)}deg`);
    };
    const resetPointer = () => {
      layer.style.setProperty('--services-type-x', '0px');
      layer.style.setProperty('--services-type-y', '0px');
      layer.style.setProperty('--services-type-r', '0deg');
    };
    hero.addEventListener('pointermove', onPointerMove);
    hero.addEventListener('pointerleave', resetPointer);

    const updateBackground = (viewport, reduced) => {
      if (!colorStops.length) return;
      let current = colorStops[0][1];
      for (let i = 1; i < colorStops.length; i += 1) {
        const [element, color] = colorStops[i];
        let t = clamp((viewport * 0.85 - element.getBoundingClientRect().top) / (viewport * 0.45), 0, 1);
        if (reduced) t = t >= 0.5 ? 1 : 0;
        current = current.map((channel, index) => lerp(channel, color[index], t));
      }
      root.style.setProperty('--st-bg', `rgb(${current.map(Math.round).join(',')})`);
    };

    const updateScroll = () => {
      frame = 0;
      const viewport = window.innerHeight || 1;
      const reduced = reduceQuery.matches;
      updateBackground(viewport, reduced);

      // Active card: the one whose centre sits closest to the middle of the viewport.
      let active = null;
      let bestDistance = Infinity;
      cards.forEach((card) => {
        const bounds = card.getBoundingClientRect();
        const centre = bounds.top + bounds.height / 2;
        const distance = Math.abs(centre - viewport / 2);
        if (bounds.top < viewport * 0.6 && bounds.bottom > viewport * 0.4 && distance < bestDistance) {
          bestDistance = distance;
          active = card;
        }
        if (!reduced) {
          card.style.setProperty('--st-card-p', clamp((viewport * 0.95 - bounds.top) / (viewport * 0.5), 0, 1).toFixed(3));
        }
      });
      cards.forEach((card) => card.classList.toggle('is-active', card === active));

      if (reduced) return;

      if (title) {
        const progress = clamp((viewport * 0.92 - title.getBoundingClientRect().top) / (viewport * 0.5), 0, 1);
        title.style.setProperty('--st-title-p', progress.toFixed(3));
      }

      // Hero words: progress through the hero drives each word's own depth offset.
      const heroBounds = hero.getBoundingClientRect();
      // Driven by scroll distance and complete within about a third of a screen, so the movement is seen while the words are still on-screen.
      const heroProgress = clamp(window.scrollY / Math.max(Math.min(heroBounds.height, viewport * 0.32), 1), 0, 1);
      layer.style.setProperty('--services-type-progress', heroProgress.toFixed(3));
      if (offers) {
        const near = clamp(1 - offers.getBoundingClientRect().top / viewport, 0, 1);
        layer.style.setProperty('--services-type-near', near.toFixed(3));
      }
    };
    const requestScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateScroll);
    };
    updateScroll();
    window.addEventListener('scroll', requestScroll, { passive: true });
    window.addEventListener('resize', requestScroll);

    const onReduceChange = () => {
      if (reduceQuery.matches) {
        resetPointer();
        layer.style.setProperty('--services-type-progress', '0');
        layer.style.setProperty('--services-type-near', '0');
        title?.style.setProperty('--st-title-p', '1');
        cards.forEach((card) => card.style.setProperty('--st-card-p', '1'));
      }
      requestScroll();
    };
    reduceQuery.addEventListener?.('change', onReduceChange);
    if (reduceQuery.matches) onReduceChange();

    const cleanup = (event) => {
      if (event.target !== root.closest('.shopify-section')) return;
      if (frame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureBands);
      hero.removeEventListener('pointermove', onPointerMove);
      hero.removeEventListener('pointerleave', resetPointer);
      window.removeEventListener('scroll', requestScroll);
      window.removeEventListener('resize', requestScroll);
      reduceQuery.removeEventListener?.('change', onReduceChange);
      root.classList.remove('services-type-ready');
      root.dataset.servicesTypeInitialized = 'false';
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
  };

  const boot = () => initialize(document.querySelector('[data-services-page]'));
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
